import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Notification } from '../../../../@posturinn/common-react/notifications';
import { closeAllModals } from '../../../../actions/modalActions';
import { getAllNotifcations, setAllNotificationsRead } from '../../../../actions/notificationsActions';
import useKeyboardEvent from '../../../../hooks/useKeyboardEvent';
import useOnClickOutside from '../../../../hooks/useOnOutsideClick/useOnOutsideClick';
import { ReactComponent as Close } from '../../../../icons/close-button.svg';
import { ReactComponent as IconParcel } from '../../../../icons/parcel.svg';
import { ReactComponent as IconRightArrow } from '../../../../icons/right-arrow.svg';
import ISettingsViewModel from '../../../../interfaces/umbracoNodes/settingsViewModel';
import { RootState } from '../../../../store';
import { A } from '../../../base/typography';
import { RawLoader } from '../../../loading/loading';
import Pagination from '../../../search/searchLandingPage/searchResults/pagination';
import { formatDate } from '../../../../helpers/date/dateHelpers';
import * as StyledModals from '../myShipmentsModals.styles';

interface Props {}

const Notifications: FC<Props> = () => {
	const { t } = useTranslation('myShipments');
	const { pageReducer } = useSelector((store: RootState) => store);
	const settingsPage: ISettingsViewModel = pageReducer.settings;
	const dispatch = useDispatch<any>();
	const {
		notifications,
		totalCount,
		totalPageCount,
		notificationsLoading,
		notificationsError,
		newNotificationsCount,
	} = useSelector(({ notificationsReducer }) => notificationsReducer);

	const [page, setPage] = useState(1);

	useEffect(() => {
		dispatch(getAllNotifcations(page));
	}, [page, dispatch]);

	const closeModal = () => {
		dispatch(setAllNotificationsRead());
		dispatch(closeAllModals());
		document.body.style.overflow = 'unset';
	};

	// Prevent body scroll while open
	useEffect(() => {
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = 'unset';
		};
	}, []);

	// ref for click-outside detection
	const wrapperRef = useRef<HTMLDivElement>(null);
	useOnClickOutside(wrapperRef, closeModal, true, 'mousedown');
	useKeyboardEvent('Escape', closeModal);

	return (
		<StyledModals.WrapperRight ref={wrapperRef}>
			<StyledModals.Heading right>
				<div>{t('notifications')}</div>
				<A onClick={closeModal}>
					<Close />
				</A>
			</StyledModals.Heading>

			{notificationsLoading && (
				<StyledModals.NotificationsEmpty>
					<RawLoader />
				</StyledModals.NotificationsEmpty>
			)}

			{!notificationsLoading && !notificationsError && totalCount !== 0
				? notifications.map((notification: Notification, index) => (
						<StyledModals.CardWrapper
							key={notification.id || index}
							old={index + 3 * (page - 1) >= newNotificationsCount}>
							<StyledModals.NotificationHeading>
								<div>
									<div>
										<IconParcel />
									</div>
									<div>{notification.title}</div>
								</div>
								<div>{formatDate(notification.timeStampUtc, 'dd.MM.yyyy')}</div>
							</StyledModals.NotificationHeading>
							<StyledModals.NotificationDetails>{notification.details}</StyledModals.NotificationDetails>
							{notification.defaultActionUrl && (
								<A
									to={
										settingsPage.Values.Shipment.Url +
										notification.defaultActionUrl.replace('SHIPMENT?SHIPMENT_', '?').toLowerCase()
									}
									onClick={closeModal}>
									<StyledModals.NotificationCallToAction>
										<IconRightArrow />
										{notification.defaultActionUrl.replace('SHIPMENT?SHIPMENT_ID=', '')}
									</StyledModals.NotificationCallToAction>
								</A>
							)}
						</StyledModals.CardWrapper>
				  ))
				: !notificationsLoading && (
						<StyledModals.NotificationsEmpty>{t('noNotifications')}</StyledModals.NotificationsEmpty>
				  )}

			{totalPageCount > 1 && (
				<Pagination
					numberOfGroups={totalPageCount}
					onChange={val => setPage(val + 1)}
					marginPagesDisplayed={1}
					pageRangeDisplayed={1}
				/>
			)}
		</StyledModals.WrapperRight>
	);
};

export default Notifications;
